import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ButtonBase, Chip, Collapse, Grid, Typography } from '@mui/material';
import { MarkdownContent } from 'components/inference/components/markdown-content';
import React, { CSSProperties, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Message } from '../../types';
import { useStyles } from './styles';
import Loader from './components/loader';

const formatQuestionTime = (date: Date): string => {
  const month = date.toLocaleString('en-US', { month: 'long' });
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${month} ${day}, ${year} ${hours}:${minutes}`;
};

type MessageContentProps = {
  message: Message,
  onViewSourceClick: () => void,
  onViewImagePreview: (image: string) => void,
  onPlaySpeech?: ({ messageId, text }: { messageId: string, text: string }) => void,
  onStopSpeech?: () => void,
  isAudioPlaying?: boolean,
  isAudioLoading?: boolean,
  textColor?: CSSProperties["color"]
  textSeconaryColor?: CSSProperties["color"]
}

export const MessageContent: FC<MessageContentProps> = ({
  message,
  onViewSourceClick,
  onPlaySpeech,
  onStopSpeech,
  onViewImagePreview,
  isAudioPlaying = false,
  isAudioLoading = false,
  textColor = "white",
  textSeconaryColor = "#FB8C00"
}) => {
  const classes = useStyles({ textColor, textSeconaryColor });
  const { t } = useTranslation();
  const [thoughtsExpanded, setThoughtsExpanded] = useState(false);
  const [toolCallsExpanded, setToolCallsExpanded] = useState(false);
  const [subAgentWorkExpanded, setSubAgentWorkExpanded] = useState(false);
  const imageKeyCounts = new Map<string, number>();
  const sourceKeyCounts = new Map<string, number>();

  const getUniqueKey = (value: string, counts: Map<string, number>): string => {
    const occurrence = counts.get(value) || 0;
    counts.set(value, occurrence + 1);
    return `${message.id}-${value}-${occurrence}`;
  };

  return (
    <Grid item container direction="column" className={classes.messageItem}>
      <Grid item container wrap="nowrap" justifyContent="space-between">
        <Grid item>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FontAwesomeIcon
              icon="comment-dots"
              className={classes.userMessageIcon}
              size="sm"
            />
            <Typography
              className={classes.userMessage}
            >
              {message.text}
            </Typography>
          </Box>
        </Grid>
        {message.response && (
        <Grid item>
          {isAudioLoading || isAudioPlaying ? (
            <ButtonBase
              onClick={onStopSpeech}
              aria-label="Stop audio"
              data-testid="audio-stop-button"
            >
              <FontAwesomeIcon
                icon="stop-circle"
                size="lg"
                color="#FB8C00"
              />
            </ButtonBase>
          ) : (
            <ButtonBase
              onClick={() => {
                if (onPlaySpeech) {
                  onPlaySpeech({ messageId: message.id, text: message.response });
                }
              }}
              data-testid="audio-play-button"
            >
              <FontAwesomeIcon
                icon="play-circle"
                size="lg"
                color="primary"
              />
            </ButtonBase>
          )}
        </Grid>
        )}
      </Grid>
      <Grid item style={{ width: "100%" }}>
        {message.thoughts && (
          <Box style={{ marginBottom: '3px' }}>
            <ButtonBase
              onClick={() => setThoughtsExpanded(!thoughtsExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 0',
                color: textSeconaryColor,
                fontSize: '0.75rem',
              }}
            >
              <FontAwesomeIcon
                icon="cog"
                size="xs"
                spin={message.isLoading}
              />
              <Typography variant="caption" style={{ color: textSeconaryColor }}>
                {t(thoughtsExpanded ? 'questionsAndAnswers:hideThoughts' : 'questionsAndAnswers:showThoughts')}
              </Typography>
              <FontAwesomeIcon
                icon={thoughtsExpanded ? 'chevron-up' : 'chevron-down'}
                size="xs"
              />
            </ButtonBase>
            <Collapse in={thoughtsExpanded}>
              <Box
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderLeft: `2px solid ${textSeconaryColor}`,
                  borderRadius: '4px',
                  padding: '8px 12px',
                  marginTop: '4px',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    margin: 0,
                  }}
                >
                  {message.thoughts}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <Box style={{ marginBottom: '3px' }}>
            <ButtonBase
              onClick={() => setToolCallsExpanded(!toolCallsExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 0',
                color: textSeconaryColor,
                fontSize: '0.75rem',
              }}
            >
              <FontAwesomeIcon
                icon="tools"
                size="xs"
              />
              <Typography variant="caption" style={{ color: textSeconaryColor }}>
                {t(toolCallsExpanded ? 'questionsAndAnswers:hideToolCalls' : 'questionsAndAnswers:showToolCalls', { count: message.toolCalls.length })}
              </Typography>
              <FontAwesomeIcon
                icon={toolCallsExpanded ? 'chevron-up' : 'chevron-down'}
                size="xs"
              />
            </ButtonBase>
            <Collapse in={toolCallsExpanded}>
              <Box
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderLeft: `2px solid ${textSeconaryColor}`,
                  borderRadius: '4px',
                  padding: '8px 12px',
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                }}
              >
                {message.toolCalls.map((tc) => {
                  const tcIcon = tc.status === 'running' ? 'spinner' : 'check-circle';
                  const tcColor = tc.status === 'running' ? textSeconaryColor : '#4caf50';
                  const tcSpin = tc.status === 'running';
                  return (
                    <Box key={tc.id} style={{ marginBottom: '8px' }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                        <FontAwesomeIcon
                          icon={tcIcon}
                          spin={tcSpin}
                          size="xs"
                          style={{ color: tcColor }}
                        />
                        <Typography
                          variant="caption"
                          style={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontWeight: 'bold',
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                          }}
                        >
                          {tc.name.startsWith('delegate_')
                            ? `→ Delegating to ${message.subAgentCalls?.find(sac => sac.status === 'running' || sac.status === 'completed')?.name ?? 'specialist'}`
                            : tc.name}
                        </Typography>
                      </Box>
                      {tc.args && (
                        <Typography
                          variant="caption"
                          component="pre"
                          style={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.6rem',
                            margin: 0,
                            paddingLeft: '16px',
                          }}
                        >
                          args:
                          {' '}
                          {tc.args}
                        </Typography>
                      )}

                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        )}
        {message.subAgentCalls && message.subAgentCalls.length > 0 && (
          <Box style={{ marginBottom: '3px' }}>
            <ButtonBase
              onClick={() => setSubAgentWorkExpanded(!subAgentWorkExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 0',
                color: textSeconaryColor,
                fontSize: '0.75rem',
              }}
            >
              <FontAwesomeIcon
                icon="network-wired"
                size="xs"
              />
              <Typography variant="caption" style={{ color: textSeconaryColor }}>
                {t(subAgentWorkExpanded ? 'questionsAndAnswers:hideSubAgentWork' : 'questionsAndAnswers:showSubAgentWork', { count: message.subAgentCalls.length })}
              </Typography>
              <FontAwesomeIcon
                icon={subAgentWorkExpanded ? 'chevron-up' : 'chevron-down'}
                size="xs"
              />
            </ButtonBase>
            <Collapse in={subAgentWorkExpanded}>
              <Box
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderLeft: `2px solid ${textSeconaryColor}`,
                  borderRadius: '4px',
                  padding: '8px 12px',
                  marginTop: '4px',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                }}
              >
                {message.subAgentCalls.map((sac) => {
                  const getIconForStatus = (status: string) => {
                    if (status === 'running') return 'spinner';
                    if (status === 'completed') return 'check-circle';
                    return 'exclamation-circle';
                  };
                  const getColorForStatus = (status: string) => {
                    if (status === 'running') return textSeconaryColor;
                    if (status === 'completed') return '#4caf50';
                    return '#ff9800';
                  };
                  const sacIcon = getIconForStatus(sac.status);
                  const sacColor = getColorForStatus(sac.status);
                  const sacSpin = sac.status === 'running';
                  return (
                    <Box key={sac.id} style={{ marginBottom: '12px' }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <FontAwesomeIcon
                          icon={sacIcon}
                          spin={sacSpin}
                          size="xs"
                          style={{ color: sacColor }}
                        />
                        <Typography
                          variant="caption"
                          style={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontWeight: 'bold',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                          }}
                        >
                          {sac.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{
                            color: 'rgba(255, 255, 255, 0.4)',
                            fontFamily: 'monospace',
                            fontSize: '0.65rem',
                            marginLeft: '4px',
                          }}
                        >
                          {sac.args}
                        </Typography>
                      </Box>
                      {sac.result && (
                        <Typography
                          variant="caption"
                          component="pre"
                          style={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.65rem',
                            margin: 0,
                            paddingLeft: '16px',
                            maxHeight: '100px',
                            overflow: 'auto',
                          }}
                        >
                          {sac.result}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        )}
        {message.isLoading && message.response.length === 0 && (
          <>
            <>
              <Loader width="40%" />
              <Loader width="90%" />
              <Loader width="85%" />
              <Loader width="80%" />
            </>
            <Grid container spacing={1}>
              <Grid item style={{ width: '20%' }}>
                <Loader />
              </Grid>
              <Grid item style={{ width: '20%' }}>
                <Loader />
              </Grid>
            </Grid>
          </>
        )}
        {message.isLoading && message.response.length > 0 && (
          <Typography
            variant="body2"
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: textColor,
            }}
          >
            {message.response}
          </Typography>
        )}
        {!message.isLoading && message.response.length > 0 && (
          <MarkdownContent
            content={message.response}
            textColor={textColor}
          />
        )}
        {message.incompleteNotice && (
          <Typography
            variant="caption"
            role="status"
            style={{ display: 'block', marginTop: '8px', color: textSeconaryColor }}
          >
            {message.incompleteNotice}
          </Typography>
        )}
      </Grid>
      <Grid item style={{ width: "100%" }}>
        {message.images !== undefined && message.images.length > 0 && (
          <Box style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '1rem',
            height: "10rem",
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'thin',
            padding: '8px 0',
          }}
          >
            {message.images.map((image) => {
              return (
                <Box
                  key={getUniqueKey(`image-${image}`, imageKeyCounts)}
                  onClick={() => onViewImagePreview(image)}
                  style={{
                    borderRadius: "5px",
                    minWidth: "6.5rem",
                    maxWidth: "10rem",
                    height: "100%",
                    flexShrink: 0,
                    overflow: "hidden",
                    border: "1.9px solid #FB8C00",
                  }}
                >
                  <img
                    src={image}
                    alt=""
                    style={{
                      objectFit: 'contain',
                      height: "100%",
                      width: "100%",
                      cursor: "pointer"
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Grid>
      <Grid item container spacing={1}>
        {message.sources && (
          <>
            {message.sources.map((source) => {
              const sourceValue = [
                source.filePath,
                source.fileName,
                source.fileType,
                source.fileSize,
                source.lastModifiedDate,
                source.lastAccessedDate,
                source.creationDate,
                source.pageLabel,
                source.text,
              ].join('|');
              return (
                <Grid item key={getUniqueKey(`source-${sourceValue}`, sourceKeyCounts)}>
                  <Chip
                    label={
                      `Source: ${source.filePath} ${source.pageLabel ? ` Page: ${source.pageLabel}` : ''}`
                    }
                    size="small"
                  />
                </Grid>
              );
            })}
            <Grid item>
              <FontAwesomeIcon
                icon="eye"
                onClick={onViewSourceClick}
                style={{ cursor: 'pointer' }}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid item style={{ justifyContent: "flex-end" }}>
        <Typography
          variant="caption"
          align="right"
          color="primary"
          className={classes.time}
        >
          {formatQuestionTime(message.questionTime)}
        </Typography>
      </Grid>
    </Grid>
  );
};
